import moment from "moment";
import { registerLocale } from "react-datepicker";

// Import moment locales more explicitly for Vite
import 'moment/dist/locale/de';
import 'moment/dist/locale/es';
import 'moment/dist/locale/fr';
import 'moment/dist/locale/it';
import 'moment/dist/locale/nl';
import 'moment/dist/locale/nl-be';

// Import common locales for date-fns
import { de } from "date-fns/locale/de";
import { enUS } from "date-fns/locale/en-US";
import { es } from "date-fns/locale/es";
import { fr } from "date-fns/locale/fr";
import { it } from "date-fns/locale/it";
import { nl } from "date-fns/locale/nl";
import { nlBE } from "date-fns/locale/nl-BE";

// Mapping of browser locales to moment.js locale codes
const momentLocaleMap: { [key: string]: string } = {
    'en': 'en',
    'en-US': 'en',
    'en-GB': 'en-gb',
    'nl': 'nl',
    'nl-BE': 'nl-be',
    'nl-NL': 'nl',
    'fr': 'fr',
    'fr-BE': 'fr',
    'fr-FR': 'fr',
    'de': 'de',
    'de-DE': 'de',
    'es': 'es',
    'es-ES': 'es',
    'it': 'it',
    'it-IT': 'it',
};

// Mapping of browser locales to date-fns locales
const localeMap: { [key: string]: any } = {
    'en': enUS,
    'en-US': enUS,
    'en-GB': enUS, // You might want to import en-GB specifically
    'nl': nl,
    'nl-BE': nlBE,
    'nl-NL': nl,
    'fr': fr,
    'fr-BE': fr,
    'fr-FR': fr,
    'de': de,
    'de-DE': de,
    'es': es,
    'es-ES': es,
    'it': it,
    'it-IT': it,
};

// Get the machine's locale
export function getMachineLocale(): string {
    // Get browser/machine locale
    const browserLocale = navigator.language || navigator.languages[0] || 'en-US';
    // console.log('Raw browser locale from navigator.language:', navigator.language);
    // console.log('Navigator.languages array:', navigator.languages);
    // console.log('Final detected locale:', browserLocale);

    return browserLocale;
}

// Get date-fns locale object for the machine's locale
export function getDateFnsLocale() {
    const machineLocale = getMachineLocale();

    // Try exact match first
    if (localeMap[machineLocale]) {
        return { locale: localeMap[machineLocale], code: machineLocale };
    }

    // Try language part only (e.g., 'en' from 'en-US')
    const languageCode = machineLocale.split('-')[0];
    if (localeMap[languageCode]) {
        return { locale: localeMap[languageCode], code: languageCode };
    }

    // Fallback to English
    return { locale: enUS, code: 'en-US' };
}

// Register the machine's locale with react-datepicker
export function registerMachineLocale() {
    const { locale, code } = getDateFnsLocale();
    registerLocale(code, locale);
    return code;
}

// Get date format based on machine locale
export function getMachineDateFormat(): string {
    const locale = getMachineLocale();

    // European locales typically use dd/MM/yyyy
    if (locale.startsWith('en-GB') ||
        locale.startsWith('nl') ||
        locale.startsWith('fr') ||
        locale.startsWith('de') ||
        locale.startsWith('es') ||
        locale.startsWith('it')) {
        return 'dd/MM/yyyy';
    }

    // US locale uses MM/dd/yyyy
    if (locale.startsWith('en-US')) {
        return 'MM/dd/yyyy';
    }

    // Default to European format
    return 'dd/MM/yyyy';
}

// Get placeholder text based on machine locale
export function getMachineDatePlaceholder(): string {
    const format = getMachineDateFormat();
    return format.toLowerCase().replace(/y/g, 'y'); // dd/mm/yyyy or mm/dd/yyyy
}

// Get moment.js locale code for the machine's locale
export function getMomentLocale(): string {
    const machineLocale = getMachineLocale();
    //console.log('Getting moment locale for machine locale:', machineLocale);

    // Try exact match first
    if (momentLocaleMap[machineLocale]) {
        //console.log('Found exact match:', momentLocaleMap[machineLocale]);
        return momentLocaleMap[machineLocale];
    }

    // Try language part only (e.g., 'en' from 'en-US')
    const languageCode = machineLocale.split('-')[0];
    //console.log('Trying language code:', languageCode);
    if (momentLocaleMap[languageCode]) {
        //console.log('Found language match:', momentLocaleMap[languageCode]);
        return momentLocaleMap[languageCode];
    }

    // Fallback to English
    //console.log('No match found, falling back to English');
    return 'en';
}

// Initialize both moment.js and date-fns with machine locale
export function initializeMachineLocale() {
    // Set moment.js locale
    const momentLocaleCode = getMomentLocale();
    // console.log('Trying to set moment locale to:', momentLocaleCode);

    // Check available locales
    // console.log('Available moment locales:', moment.locales());

    // Try setting the locale and verify it worked
    const setResult = moment.locale(momentLocaleCode);
    // console.log('Moment.locale() set result:', setResult);
    // console.log('Actual moment locale after setting:', moment.locale());

    // If setting failed, try alternative approaches
    if (moment.locale() === 'en' && momentLocaleCode !== 'en') {
        // console.log('Primary locale setting failed, trying alternatives...');

        // Try with different variations
        const alternatives = [momentLocaleCode.toLowerCase(), momentLocaleCode.toUpperCase(), 'nl-be', 'nl'];
        for (const alt of alternatives) {
            const altResult = moment.locale(alt);
            // console.log(`Trying ${alt}: result = ${altResult}, current = ${moment.locale()}`);
            if (moment.locale() !== 'en') {
                break;
            }
        }
    }

    // Register date-fns locale for react-datepicker
    const dateFnsLocaleCode = registerMachineLocale();

    return {
        moment: momentLocaleCode,
        dateFns: dateFnsLocaleCode
    };
}